import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { SessionProvider } from '../features/auth/session';
import { AuthGate, SignedOutOnly } from './AuthGate';
import { AppErrorBoundary } from './AppErrorBoundary';
import { SignInScreen } from '../features/auth/SignInScreen';
import { ForgotPasswordScreen } from '../features/auth/ForgotPasswordScreen';
import { ResetPasswordScreen } from '../features/auth/ResetPasswordScreen';
import { ProjectsScreen } from '../features/projects/ProjectsScreen';
import { ProjectHomeScreen } from '../features/projects/ProjectHomeScreen';
import { CaptureCreateScreen } from '../features/captures/CaptureCreateScreen';
import { CaptureDetailScreen } from '../features/captures/CaptureDetailScreen';
import { AskSolScreen } from '../features/sol/AskSolScreen';
import { GlobalSolScreen } from '../features/sol/GlobalSolScreen';
import { SolHistoryScreen } from '../features/sol/SolHistoryScreen';
import { SolMemoryScreen } from '../features/sol/SolMemoryScreen';
import { GLOBAL_SOL_PATH, SOL_MEMORY_PATH } from '../features/sol/sol-routes';
import { SettingsScreen } from '../features/settings/SettingsScreen';
import { SETTINGS_PATH } from '../features/settings/settings-route';
import { MyDayScreen } from '../features/my-day/MyDayScreen';
import { MyDayHistoryScreen } from '../features/my-day/MyDayHistoryScreen';
import { MY_DAY_HISTORY_PATH, MY_DAY_PATH } from '../features/my-day/my-day-route';
import { ReportBuilderLayout } from '../features/reports/ReportBuilderLayout';
import { ReportCaptureStep } from '../features/reports/ReportCaptureStep';
import { ReportPhotoStep } from '../features/reports/ReportPhotoStep';
import { ReportPreviewStep } from '../features/reports/ReportPreviewStep';
import { SavedReportsScreen } from '../features/reports/saved/SavedReportsScreen';
import { SavedReportScreen } from '../features/reports/saved/SavedReportScreen';
import { SolSessionProvider } from '../features/sol/sol-session';
import { SystemBackBridge } from './SystemBackBridge';
import { MyDayNotificationsBridge } from './MyDayNotificationsBridge';

/**
 * Application shell.
 *
 * Routes present in S-01 and nothing more. Projects is the default entry
 * (PT §3); everything else in the product arrives with its own milestone.
 *
 * `/reset-password` is intentionally reachable while signed out AND while
 * signed in: the recovery link establishes a session in order to change the
 * password, so gating it either way would break the flow.
 */
export function App() {
  return (
    <AppErrorBoundary>
      <BrowserRouter>
        {/* ANDROID'S BACK GESTURE, JOINED TO THIS ROUTER (M10 / S10-03B).

            INSIDE `BrowserRouter` because it navigates, and ABOVE
            `SessionProvider` because Back is not a signed-in privilege — the
            sign-in and password-recovery screens are ordinary history too, and
            a Back that only worked once signed in would close the app from the
            screens where a confused user presses it most.

            It renders nothing. On the web it does nothing at all: a browser tab
            owns its own Back button and a page has no business intercepting it.
            The platform decision lives in `src/platform/system-back.ts`. */}
        <SystemBackBridge />
        {/* WD-3: startup/foreground reconciliation and notification-tap
            routing. Inert off native; signed out its run is a no-op. */}
        <MyDayNotificationsBridge />
        <SessionProvider>
          {/* THE CURRENT SOL VISIT, HELD ABOVE THE ROUTES (M9 / S09-04).

              Opening a source Capture to check it is part of the SAME Sol task,
              and it used to destroy the answer — on a real phone, Brandon had
              to retype the question and pay for a second reasoning call just to
              look at the second piece of evidence. The visit lives here so
              navigating cannot throw it away.

              **IN MEMORY ONLY. NOT conversational memory.** Nothing is
              persisted anywhere, and no earlier exchange is ever sent to Sol —
              every request carries one question and the Project on screen.
              INSIDE `SessionProvider` deliberately: signing out ends the visit. */}
          <SolSessionProvider>
            <Routes>
              <Route
                path="/sign-in"
                element={
                  <SignedOutOnly>
                    <SignInScreen />
                  </SignedOutOnly>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <SignedOutOnly>
                    <ForgotPasswordScreen />
                  </SignedOutOnly>
                }
              />
              <Route path="/reset-password" element={<ResetPasswordScreen />} />
              <Route
                path="/"
                element={
                  <AuthGate>
                    <ProjectsScreen />
                  </AuthGate>
                }
              />
              {/* Project Home. Gated like every protected screen — the id in the
                URL grants nothing, because the read is filtered by RLS. */}
              <Route
                path="/projects/:projectId"
                element={
                  <AuthGate>
                    <ProjectHomeScreen />
                  </AuthGate>
                }
              />
              {/* Typed Fast Capture and read-only Capture Detail (M3 / S-03-02).
                Gated like every protected screen. The ids in the URL grant
                nothing: both reads are filtered by RLS, and create_capture
                verifies ownership from auth.uid() rather than from the path. */}
              <Route
                path="/projects/:projectId/capture"
                element={
                  <AuthGate>
                    <CaptureCreateScreen />
                  </AuthGate>
                }
              />
              <Route
                path="/projects/:projectId/captures/:captureId"
                element={
                  <AuthGate>
                    <CaptureDetailScreen />
                  </AuthGate>
                }
              />
              {/* Ask Sol (M9 / S09-04). Read-only: Sol answers from authorized
                Captures and performs no writes and no actions. The Project id
                is CONTEXT for the question, not permission — `sol-ask`
                resolves scope from the caller's own token, and every read it
                makes travels on that token through forced RLS.

                The route is Project-shaped because that is where Sol is
                entered today. The screen behind it is not: `SolConversation`
                takes Project context as props, so a future global /sol reuses
                it unchanged. */}
              <Route
                path="/projects/:projectId/sol"
                element={
                  <AuthGate>
                    <AskSolScreen />
                  </AuthGate>
                }
              />
              {/* M11 / D-040, D-041 — conversation history, long-term memory,
                  and the REAL Global Sol. **One Sol**: `/sol` mounts the same
                  `SolConversation` with no Project on screen; `/c/:conversationId`
                  reopens an earlier conversation where it started; `/history`
                  lists one origin's conversations (Global search crosses them);
                  `/sol/memory` is what Sol remembers. The ids in these addresses
                  grant nothing — every read behind them is RLS-bound and every
                  write goes through an owner-scoped writer behind the screen's
                  own confirmation. The paths come from `sol-routes.ts`. */}
              <Route
                path="/projects/:projectId/sol/c/:conversationId"
                element={
                  <AuthGate>
                    <AskSolScreen />
                  </AuthGate>
                }
              />
              <Route
                path="/projects/:projectId/sol/history"
                element={
                  <AuthGate>
                    <SolHistoryScreen />
                  </AuthGate>
                }
              />
              <Route
                path={GLOBAL_SOL_PATH}
                element={
                  <AuthGate>
                    <GlobalSolScreen />
                  </AuthGate>
                }
              />
              <Route
                path={`${GLOBAL_SOL_PATH}/c/:conversationId`}
                element={
                  <AuthGate>
                    <GlobalSolScreen />
                  </AuthGate>
                }
              />
              <Route
                path={`${GLOBAL_SOL_PATH}/history`}
                element={
                  <AuthGate>
                    <SolHistoryScreen />
                  </AuthGate>
                }
              />
              <Route
                path={SOL_MEMORY_PATH}
                element={
                  <AuthGate>
                    <SolMemoryScreen />
                  </AuthGate>
                }
              />

              {/* Settings (M10 / S10-03A). Gated like every protected screen.
                **Project-independent on purpose**: company branding belongs to
                Brandon's business, not to any one job, so this path carries no
                Project id. It is the smallest surface that can own company
                branding — the general Settings screen the roadmap places in M12
                adds sections here rather than inventing the screen. */}
              {/* MY DAY (M12 / S12-01). Gated like every protected screen.
                **Project-independent on purpose**: a follow-up may point at a
                Project, but the list of what Brandon still has to do is not a
                property of any one job. No id in the path grants anything —
                the read is RLS-filtered and the writers take their identity
                from auth.uid(). */}
              <Route
                path={MY_DAY_PATH}
                element={
                  <AuthGate>
                    <MyDayScreen />
                  </AuthGate>
                }
              />
              {/* MY DAY HISTORY (WD-1, 0030). The append-only event log,
                read-only, last 100 newest first — the bound and the order are
                the database's. Same gate, same RLS story as My Day itself. */}
              <Route
                path={MY_DAY_HISTORY_PATH}
                element={
                  <AuthGate>
                    <MyDayHistoryScreen />
                  </AuthGate>
                }
              />

              <Route
                path={SETTINGS_PATH}
                element={
                  <AuthGate>
                    <SettingsScreen />
                  </AuthGate>
                }
              />

              {/* The report builder (M10 / S10-01, given real steps by
                S10-02). Gated like every protected screen. The id in the URL
                grants nothing — every read behind it is filtered by forced RLS.

                **TWO REAL ADDRESSES, ONE PER STEP, AND THAT IS THE POINT.**
                Android's Back gesture reaches this history through
                `SystemBackBridge` above. While the second step was a variable
                inside one screen it was invisible to history, and Back left the
                builder entirely — losing whatever Brandon had chosen. As
                separate routes, Back walks between the steps instead.

                **CORRECTED 2026-08-19 (M10 / S10-03B).** This comment used to
                say Crewlink installed no App plugin "so Android hands Back
                straight to the web view". The first half was true; the second
                did not follow, and was wrong. With no handler registered,
                nothing received Back at all and Android finished the Activity —
                on Jerry's phone that closed the app and destroyed the report.

                **`ReportBuilderLayout` HOLDS THE IN-PROGRESS REPORT AND MUST
                STAY THE PARENT.** React Router wraps a parent route around its
                child with no identity key, so the layout is NOT re-mounted when
                only the step changes — which is exactly what preserves the
                Captures, photos and Description across a Back. Flattening these
                into sibling routes would silently reintroduce the bug.

                The paths come from `report-route.ts`; they are written
                literally here because a Route's `path` is a pattern rather than
                a URL, and the tests assert the two against each other. */}
              <Route
                path="/projects/:projectId/report"
                element={
                  <AuthGate>
                    <ReportBuilderLayout />
                  </AuthGate>
                }
              >
                <Route index element={<ReportCaptureStep />} />
                <Route path="photos" element={<ReportPhotoStep />} />
                <Route path="preview" element={<ReportPreviewStep />} />
              </Route>

              {/* The pre-M5 photo probe (Risk Probe C) lived at /probe/photo
                until 2026-08-14, when the probe PASSED on real Android
                hardware and was retired — screen, sanitizer, upload harness
                and route together. /probe/photo is now an unknown path like
                any other. Its findings survive in BUILD_LOG.md and in
                I-040 / I-041 / I-042; none of its code was promoted, and M5
                builds the real photo pipeline from scratch. */}

              {/* The M2 development-only recorder probe lived here until
                M3 / S-03-03, when the real Capture recording flow replaced it
                and the probe was retired — screen, launcher, route and flag
                together. /probe/recorder is now an unknown path like any
                other. The RecorderPort the probe validated lives on in
                src/platform/recorder.ts, behind the Capture screen. */}

              {/* Unknown paths go to the default entry, which is itself gated. */}
              {/* SAVED REPORTS (M10 / S10-05). Gated like every protected
                  screen. **Two sibling routes, not a nested builder**: a saved
                  report is a finished document, so there is no in-progress
                  state for a layout to preserve — the opposite of the report
                  builder, whose steps must stay children of one layout. */}
              <Route
                path="/projects/:projectId/reports"
                element={
                  <AuthGate>
                    <SavedReportsScreen />
                  </AuthGate>
                }
              />
              <Route
                path="/projects/:projectId/reports/:reportId"
                element={
                  <AuthGate>
                    <SavedReportScreen />
                  </AuthGate>
                }
              />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </SolSessionProvider>
        </SessionProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}
